import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useCreate, useNotify, useRedirect } from 'react-admin';
import { InsuranceFormFields } from './InsuranceFormFields';

interface InsuranceCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

export const InsuranceCreateDialog = ({ open, onClose }: InsuranceCreateDialogProps) => {
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const redirect = useRedirect();

  const [formData, setFormData] = React.useState({
    policyNumber: '',
    insurerName: '',
    policyName: '',
    effectiveDate: '',
    expiryDate: '',
    active: true,
    liabilityLimit: '',
    collisionDeductible: '',
    comprehensiveDeductible: '',
    allPerilsDeductible: '',
    notes: '',
  });

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.policyNumber || !formData.insurerName || !formData.effectiveDate || !formData.expiryDate) {
      notify('Please fill in all required fields', { type: 'warning' });
      return;
    }

    const dataToSubmit: any = {
      policyNumber: formData.policyNumber,
      insurerName: formData.insurerName,
      effectiveDate: formData.effectiveDate,
      expiryDate: formData.expiryDate,
      active: formData.active,
    };

    if (formData.policyName) dataToSubmit.policyName = formData.policyName;
    if (formData.liabilityLimit) dataToSubmit.liabilityLimit = Number(formData.liabilityLimit);
    if (formData.collisionDeductible) dataToSubmit.collisionDeductible = Number(formData.collisionDeductible);
    if (formData.comprehensiveDeductible) dataToSubmit.comprehensiveDeductible = Number(formData.comprehensiveDeductible);
    if (formData.allPerilsDeductible) dataToSubmit.allPerilsDeductible = Number(formData.allPerilsDeductible);
    if (formData.notes) dataToSubmit.notes = formData.notes;

    create(
      'insurance',
      { data: dataToSubmit },
      {
        onSuccess: () => {
          notify('Insurance policy created successfully', { type: 'success' });
          onClose();
          setFormData({
            policyNumber: '',
            insurerName: '',
            policyName: '',
            effectiveDate: '',
            expiryDate: '',
            active: true,
            liabilityLimit: '',
            collisionDeductible: '',
            comprehensiveDeductible: '',
            allPerilsDeductible: '',
            notes: '',
          });
          redirect('/insurance');
        },
        onError: (error: any) => {
          notify(`Error creating insurance policy: ${error.message}`, { type: 'error' });
        },
      }
    );
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Insurance Policy</DialogTitle>
      <DialogContent>
        <InsuranceFormFields formData={formData} handleChange={handleChange} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          Create Policy
        </Button>
      </DialogActions>
    </Dialog>
  );
};
